<script lang="ts">
import { PropType, computed, defineComponent, h, onMounted, ref } from 'vue';
import { Fragment } from 'vue-frag'

export type ImageResolution =
	| 'default'
	| 'mqdefault'
	| 'hqdefault'
	| 'sddefault'
	| 'maxresdefault'

function runCommand(iframe: HTMLIFrameElement | null, func: 'stopVideo' | 'pauseVideo' | 'playVideo') {
	if (iframe === null)
		throw new Error('iframe element not instantiated.')

	iframe.contentWindow?.postMessage(`{"event":"command","func":"${func}","args":""}`, '*')
}

function linkPreconnect(href: string) {
	return h('link', {
		attrs: { rel: 'preconnect', href },
	})
}

export default defineComponent({
	name: 'VideoPlayer',
	components: { Fragment },
	props: {
		announce: {
			type: String,
			required: false,
			default: 'Watch',
		},
		id: {
			type: String,
			required: true,
		},
		title: {
			type: String
		},
		activatedClass: {
			type: String,
			required: false,
			default: 'lyt-activated',
		},
		adNetwork: {
			type: Boolean,
			required: false,
			default: true,
		},
		iframeClass: {
			type: String,
			required: false,
			default: '',
		},
		cookie: {
			type: Boolean,
			required: false,
			default: false,
		},
		autoplay: {
			type: Boolean,
			default: false
		},
		lazy: {
			type: Boolean,
			default: true
		},
		playerClass: {
			type: String,
			required: false,
			default: 'lty-playbtn',
		},
		playlist: {
			type: Boolean,
			required: false,
			default: false,
		},
		playlistCoverId: {
			type: String,
			required: false,
			default: '',
		},
		poster: {
			type: String as PropType<ImageResolution>,
			required: false,
			default: 'hqdefault',
		},
		wrapperClass: {
			type: String,
			required: false,
			default: 'yt-lite',
		},
		muted: {
			type: Boolean,
			required: false,
			default: false,
		},
		thumbnail: {
			type: String,
			required: false,
		},
		webp: {
			type: Boolean,
			required: false,
			default: false,
		},
		rel: {
			type: String as PropType<'prefetch' | 'preload'>,
			required: false,
			default: 'preload',
		},
		aspectHeight: {
			type: Number,
			required: false,
			default: 9,
		},
		aspectWidth: {
			type: Number,
			required: false,
			default: 16,
		},
	},
	emits: ['iframeAdded'],
	setup(props, { emit, expose }) {
		const preconnected = ref(false)
		const iframe = ref(false)
		const iframeElement = ref<HTMLIFrameElement | null>(null)

		const videoId = computed(() => encodeURIComponent(props.id))
		const autoplayImp = computed(() => props.autoplay ? '&autoplay=true' : '')
		const mutedImp = computed(() => props.muted ? '&mute=1' : '')
		const format = computed(() => props.webp ? 'webp' : 'jpg')
		const vi = computed(() => props.webp ? 'vi_webp' : 'vi')
		const videoPlaylistCoverId = computed(() => typeof props.playlistCoverId === 'string' ? encodeURIComponent(props.playlistCoverId) : null)

		const posterUrl = computed(() => {
			return props.thumbnail || (!props.playlist
				? `https://i.ytimg.com/${vi.value}/${videoId.value}/${props.poster}.${format.value}`
				: `https://i.ytimg.com/${vi.value}/${videoPlaylistCoverId.value}/${props.poster}.${format.value}`)
		})

		const ytUrl = computed(() => props.cookie
			? 'https://www.youtube.com'
			: 'https://www.youtube-nocookie.com')
		const iframeSrc = computed(() => !props.playlist
			? `${ytUrl.value}/embed/${videoId.value}?autoplay=1&enablejsapi=1&state=1${mutedImp.value}${autoplayImp.value}`
			: `${ytUrl.value}/embed/videoseries?autoplay=1&enablejsapi=1&list=${videoId.value}${mutedImp.value}${autoplayImp.value}`)

		function addIframe() {
			if (iframe.value)
				return
			iframe.value = true
			emit('iframeAdded')
		}

		function warmConnections() {
			if (preconnected.value)
				return
			preconnected.value = true
		}

		expose({
			getPlayerInstance() {
				return iframeElement.value
			},
			stopVideo() {
				runCommand(iframeElement.value, 'stopVideo')
			},
			pauseVideo() {
				runCommand(iframeElement.value, 'pauseVideo')
			},
			playVideo() {
				runCommand(iframeElement.value, 'playVideo')
			},
			warmConnections,
			addIframe,
		})

		onMounted(() => (!props.lazy || props.autoplay) ? addIframe() : {});

		const vnodeList = (): any[] => [
			h('link', {
				attrs: {
					rel: props.rel,
					href: posterUrl.value,
					as: 'image',
				},
			}),
			preconnected.value ? linkPreconnect(ytUrl.value) : null,
			preconnected.value ? linkPreconnect('https://www.google.com') : null,
			props.adNetwork ? linkPreconnect('https://static.doubleclick.net') : null,
			props.adNetwork
				? linkPreconnect('https://googleads.g.doubleclick.net')
				: null,
			h(
				'article',
				{
					on: {
						pointerover: warmConnections,
						click: addIframe,
					},
					class: `${props.wrapperClass} ${iframe.value ? props.activatedClass : ''}`,
					attrs: { 'data-title': props.title },
					style: {
						'backgroundImage': `url(${posterUrl.value})`,
						'--aspect-ratio': `${(props.aspectHeight / props.aspectWidth) * 100}%`,
					},
				},
				[
					// Play button
					h('button', {
						class: props.playerClass,
						attrs: {
							'type': 'button',
							'aria-label': `${props.announce} ${props.title}`,
						},
					}),
					// Iframe
					iframe.value
						? h('iframe', {
							ref: iframeElement,
							class: props.iframeClass,
							attrs: {
								title: props.title,
								width: 560,
								height: 315,
								frameborder: 0,
								allow:
									'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture',
								allowfullscreen: true,
								src: iframeSrc.value,
							},
						})
						: null,
				],
			),
		]

		return () => h('Fragment', {}, vnodeList())
	}
})

</script>


<style scoped>
.yt-lite {
	background-color: #000;
	position: relative;
	display: block;
	contain: content;
	background-position: 50%;
	background-size: cover;
	cursor: pointer;
}

.yt-lite:after {
	content: "";
	display: block;
	padding-bottom: 56.25%;
}

.yt-lite>iframe {
	width: 100%;
	height: 100%;
	position: absolute;
	top: 0;
	left: 0;
}

.yt-lite>.lty-playbtn {
	display: block;
	width: 68px;
	height: 48px;
	position: absolute;
	cursor: pointer;
	transform: translate3d(-50%, -50%, 0);
	top: 50%;
	left: 50%;
	z-index: 1;
	background-color: transparent;
	/* YT's actual play button svg */
	background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 68 48"><path d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55c-2.93.78-4.63 3.26-5.42 6.19C.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z" fill="red"/><path d="M45 24 27 14v20" fill="white"/></svg>');
	filter: grayscale(100%);
	transition: filter 0.2s cubic-bezier(0, 0, 0.2, 1);
	border: none;
}

.yt-lite:hover>.lty-playbtn,
.yt-lite:focus-within>.lty-playbtn {
	filter: none;
}

.yt-lite.lyt-activated {
	cursor: unset;
}

.yt-lite.lyt-activated:before,
.yt-lite.lyt-activated>.lty-playbtn {
	opacity: 0;
	pointer-events: none;
}</style>
